import React, { memo, useMemo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import routes from "@/routes/routes";
import styles from "./Navbar.module.scss";
import { getCurrentUser, addBaseUrl } from "@/lib/utils";
import { useTheme } from "@/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { setSidebarOpen } from "@/store/slices/globalSlice";
import { fetchOrgSettings } from "@/store/slices/organizationSlice";
import { type RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";

/* ── Inline SVG icons (stroke-based, 1.7 weight) ── */
const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const SunIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const BellIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const Navbar: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const sidebarOpen = useSelector((state: RootState) => state.global.sidebarOpen);
  const { profile } = useSelector((state: RootState) => state.user);
  const { settings: orgSettings } = useSelector((state: RootState) => state.organization);
  const cachedUser = getCurrentUser();

  const [logoError, setLogoError] = useState(false);

  // Fetch organization settings on mount
  useEffect(() => {
    if (!orgSettings) {
      dispatch(fetchOrgSettings());
    }
  }, [dispatch, orgSettings]);

  useEffect(() => {
    setLogoError(false);
  }, [orgSettings?.organizationProfileURL]);

  const displayName =
    profile?.name ||
    (profile as any)?.firstName ||
    (profile as any)?.fullName ||
    cachedUser?.userName ||
    "Developer";
  const displayRole = (profile as any)?.role || cachedUser?.role || "USER";
  const avatarUrl = profile?.profileUrl ? addBaseUrl(profile.profileUrl) : null;
  const isAdminOrOwner = displayRole.toUpperCase() === "OWNER" || displayRole.toUpperCase() === "ADMIN";
  const isOwnerOrManager = displayRole.toUpperCase() === "OWNER" || displayRole.toUpperCase() === "MANAGER" || displayRole.toUpperCase() === "ADMIN";

  const toggleSidebar = () => {
    dispatch(setSidebarOpen(!sidebarOpen));
  };

  /* Derive page name from route config — same logic as before */
  const removeLastEndpoint = (str: string): string =>
    str.split("/").slice(0, -1).join("/");

  const pageName = useMemo(() => {
    const obj = Object.values(routes.privateRoutes)
      .flatMap((x: any) =>
        x?.children
          ? Object.values(x.children).map((y: any) => ({
            ...y,
            path: `${x.path}/${y.path}`,
          }))
          : [x]
      )
      .find((pathData: any) => {
        return (
          pathData.path === location.pathname ||
          (pathData.path.endsWith("Id") &&
            removeLastEndpoint(pathData.path) ===
            removeLastEndpoint(location.pathname))
        );
      });
    return (obj as any)?.pageName || "";
  }, [location]);

  return (
    <header className={styles.topbar}>
      {/* ── Left: hamburger + page title ── */}
      <div className={styles.left}>
        <button
          className={styles.iconBtn}
          onClick={toggleSidebar}
          title="Toggle sidebar"
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>
        <span className={styles.pageTitle}>{pageName}</span>
      </div>

      {/* ── Right: organization · theme toggle · bell · user name · avatar ── */}
      <div className={styles.right}>
        {/* {orgSettings?.name && (
          <div
            className={`${styles.orgChip} ${isAdminOrOwner ? styles.editable : ""}`}
            onClick={isAdminOrOwner ? () => navigate("/roles-permissions/organization-settings") : undefined}
            title={isAdminOrOwner ? "Edit Organization Settings" : "Organization"}
            role={isAdminOrOwner ? "button" : undefined}
            tabIndex={isAdminOrOwner ? 0 : undefined}
            onKeyDown={isAdminOrOwner ? (e) => e.key === "Enter" && navigate("/roles-permissions/organization-settings") : undefined}
          >
            <span className={styles.orgLabel}>Organization</span>
            <span className={styles.orgName}>{orgSettings.name}</span>
          </div>
        )} */}

        {/* Theme toggle */}
        <button
          className={styles.iconBtn}
          onClick={toggleTheme}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          aria-label="Toggle theme"
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        {/* Notification bell */}
        {/* <button className={styles.iconBtn} title="Notifications" aria-label="Notifications">
          <BellIcon />
        </button> */}

        {/* User name + avatar — click to go to profile */}
        {/* <div
          className={styles.userChip}
          onClick={() => navigate("/roles-permissions/personal-info")}
          title="View profile"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/roles-permissions/personal-info")}
        >
          <div className={styles.userInfo}>
            <span className={styles.userName}>{displayName}</span>
            <span className={styles.userRole}>{displayRole}</span>
          </div>
          <div className={styles.avatar}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className={styles.avatarImg} />
            ) : (
              <span>{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>
        </div> */}

        {orgSettings?.name && (
          <div
            className={`${styles.orgChip} ${isOwnerOrManager ? styles.editable : ""}`}
            onClick={() => navigate("/roles-permissions/organization-settings")}
            title={isOwnerOrManager ? "Edit Organization Settings" : "View Organization"}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/roles-permissions/organization-settings")}
          >
            <div className={styles.orgAvatar}>
              {orgSettings.organizationProfileURL && !logoError ? (
                <img 
                  src={addBaseUrl(orgSettings.organizationProfileURL)} 
                  alt={orgSettings.name} 
                  className={styles.orgAvatarImg} 
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span>{orgSettings.name.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <span className={styles.orgName} title={orgSettings.name}>
              {orgSettings.name.length > 20 ? `${orgSettings.name.slice(0, 20)}...` : orgSettings.name}
            </span>
          </div>
        )}
      </div>
    </header>
  );
};

export default memo(Navbar);
